import { Flex, Input, Tag } from 'antd';
import { Key, useEffect, useMemo, useRef, useState } from 'react';

import { $Color } from '@suntex.core/animation';
import { isPlayerLoginType } from '@suntex.fim/auth';
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react';
import { FavoriteGamesAction, LoginType } from '@suntex.fim/enums';
import {
  ModuleTagging,
  ModuleWithDirections,
  buildUnderConstructionSet,
} from '@suntex.fim/framework';
import {
  ModuleTag,
  StarValue,
  StrictNodeModel,
  icons,
  rootRadialData,
  withFromParam,
} from '@suntex.fim/hub';
import { Button, Toolbar, Tree, type TreeDataNode } from '@suntex.fim/ui';
import { SearchIcon } from '@suntex.fim/ui/icons';

import {
  useCategoryStarValue,
  useModuleSearch,
  useModuleStarValue,
} from '../hooks';
import '../style/ModuleCatalog.scss';
import {
  getEffectiveChildren,
  getLabelText,
  isNodeUnderConstruction,
} from '../utils/moduleTree';

import { ComingSoon } from './ComingSoon';
import { DirectionsButton } from './DirectionsButton';
import { FavoriteButton } from './FavoriteButton';
import { GameActions } from './GameLibrary/GameActions';
import { HelpText } from './HelpText';
import { ProgressStar } from './ProgressStar';

export interface ModuleCatalogProps {
  modules: readonly ModuleWithDirections[];
  moduleTagging: readonly ModuleTagging[];
  path: string | undefined;
}

// Gap (px) left below the tree so it stops short of the window bottom.
const TREE_BOTTOM_GAP = 16;

export function ModuleCatalog({
  modules,
  path,
  moduleTagging,
}: ModuleCatalogProps) {
  const { login } = useAuth(isAuthenticated);
  const {
    searchTerm,
    setSearchTerm,
    showFavoritesOnly,
    setShowFavoritesOnly,
    isFavorite,
    toggleFavorite,
    matchesSearch,
  } = useModuleSearch(modules, moduleTagging);
  const getModuleStarValue = useModuleStarValue();
  const getCategoryStarValue = useCategoryStarValue(moduleTagging);
  const moduleMap = useMemo(
    () => buildModuleMap(rootRadialData, modules, moduleTagging),
    [modules, moduleTagging],
  );
  const underConstructionSet = useMemo(
    () => buildUnderConstructionSet(moduleTagging),
    [moduleTagging],
  );
  const treeDataRoot = useMemo(
    () =>
      toTreeDataNode(rootRadialData, undefined, {
        loginType: login.loginType,
        moduleMap,
        moduleTagging,
        underConstructionSet,
        isFavorite,
        toggleFavorite,
        getModuleStarValue,
        getCategoryStarValue,
      }),
    [
      login.loginType,
      moduleMap,
      moduleTagging,
      underConstructionSet,
      isFavorite,
      toggleFavorite,
      getModuleStarValue,
      getCategoryStarValue,
    ],
  );

  const filteredTreeData = useMemo(() => {
    const filterNodes = (nodes: TreeDataNode[]): TreeDataNode[] => {
      const filtered: TreeDataNode[] = [];

      nodes.forEach((node) => {
        const { searchText, module } = moduleMap[`${node.key}`] ?? {};
        const nodePassesFilters = module
          ? matchesSearch(module.slug, searchText)
          : false;

        const filteredChildren = node.children
          ? filterNodes(node.children)
          : [];

        if (nodePassesFilters || filteredChildren.length > 0) {
          filtered.push({
            ...node,
            children:
              filteredChildren.length > 0 ? filteredChildren : node.children,
          });
        }
      });

      return filtered;
    };

    return filterNodes(treeDataRoot?.children || []);
  }, [matchesSearch, moduleMap, treeDataRoot]);

  const [expandedKeys, setExpandedKeys] = useState<Key[]>(path ? [path] : []);
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  // Fill the window below the tree. A set height also turns on antd's
  // virtual scroll, which keeps "Expand all" smooth.
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const [treeHeight, setTreeHeight] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = treeContainerRef.current;
    if (!el) return;
    const update = () => {
      const { top } = el.getBoundingClientRect();
      setTreeHeight(Math.max(0, window.innerHeight - top - TREE_BOTTOM_GAP));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const allKeys = useMemo(
    () => collectKeys(filteredTreeData),
    [filteredTreeData],
  );

  const expandAll = () => {
    setExpandedKeys(allKeys);
    setAutoExpandParent(false);
  };

  const collapseAll = () => {
    setExpandedKeys([]);
    setAutoExpandParent(false);
  };

  return (
    <div className="module-catalog">
      <Toolbar
        right={
          <Flex gap={8} align="center">
            <Button intent="text" size="small" onClick={expandAll}>
              Expand all
            </Button>
            <Button intent="text" size="small" onClick={collapseAll}>
              Collapse all
            </Button>
            <FavoriteButton
              active={showFavoritesOnly}
              onClick={() => setShowFavoritesOnly((v) => !v)}
              aria-label={
                showFavoritesOnly ? 'Show all modules' : 'Show favorites only'
              }
            />
            <Input
              placeholder="Filter Modules"
              prefix={<SearchIcon />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Flex>
        }
      />
      <div ref={treeContainerRef}>
        <Tree
          treeData={filteredTreeData}
          blockNode
          height={treeHeight}
          expandedKeys={expandedKeys}
          onExpand={(keys) => {
            setExpandedKeys(keys);
            setAutoExpandParent(false);
          }}
          autoExpandParent={autoExpandParent}
          selectable={false}
          expandAction={'click'}
        />
      </div>
    </div>
  );
}

function collectKeys(nodes: TreeDataNode[]): Key[] {
  const keys: Key[] = [];
  nodes.forEach((node) => {
    if (node.children) {
      keys.push(node.key);
      keys.push(...collectKeys(node.children));
    }
  });
  return keys;
}

function buildModuleMap(
  node: StrictNodeModel,
  modules: readonly ModuleWithDirections[],
  moduleTagging: readonly ModuleTagging[],
): Record<
  string,
  | {
      module: ModuleWithDirections;
      tags: ModuleTag[];
      searchText: string;
    }
  | undefined
> {
  const module = modules.find((m) => m.slug === node.moduleSlug);
  const descendents = getEffectiveChildren(node, moduleTagging).reduce(
    (acc, n) => Object.assign(acc, buildModuleMap(n, modules, moduleTagging)),
    {} as Record<
      string,
      | {
          module: ModuleWithDirections;
          tags: ModuleTag[];
          searchText: string;
        }
      | undefined
    >,
  );

  if (!module) {
    return descendents;
  }

  const tags = moduleTagging
    .filter((t) => t.module.slug === node.moduleSlug)
    .map((t) => t.tag);
  return {
    [node.key]: {
      module,
      tags,
      searchText: [
        getLabelText(node),
        node.tag,
        node.moduleSlug,
        node.info?.title,
        node.info?.summary,
        module.name,
        module.description,
        module.ariaDescription,
        ...tags.map((t) => t.path),
      ]
        .filter(Boolean)
        .join('\n')
        .toLowerCase(),
    },
    ...descendents,
  };
}

function toTreeDataNode(
  node: StrictNodeModel,
  parentKey: string | undefined,
  ctx: {
    loginType: LoginType;
    moduleMap: Record<
      string,
      { module: ModuleWithDirections; tags: ModuleTag[] } | undefined
    >;
    moduleTagging: readonly ModuleTagging[];
    underConstructionSet: Set<string>;
    isFavorite: (slug: string) => boolean;
    toggleFavorite: (
      slug: string,
      action: FavoriteGamesAction.Add | FavoriteGamesAction.Remove,
    ) => Promise<void>;
    getModuleStarValue: (
      node: Pick<StrictNodeModel, 'moduleSlug' | 'dataPath' | 'dataTarget'>,
    ) => StarValue;
    getCategoryStarValue: (node: StrictNodeModel) => StarValue;
  },
  parentAccessibleLabel?: string,
): TreeDataNode | null {
  const { module, tags } = ctx.moduleMap[node.key] ?? {};

  // omit modules that have been filtered out
  if (node.moduleSlug && !module) {
    return null;
  }

  const labelText = getLabelText(node) ?? module?.description;
  const nodeAccessibleLabel =
    labelText ?? module?.name ?? node.info?.title ?? node.key;
  const accessibleLabel = parentAccessibleLabel
    ? `${parentAccessibleLabel}, ${nodeAccessibleLabel}`
    : nodeAccessibleLabel;

  const displayTags = Array.from(
    new Set(tags?.map((t) => t.path) ?? []),
  ).sort();

  const underConstruction = isNodeUnderConstruction(
    node,
    ctx.moduleTagging,
    ctx.underConstructionSet,
  );

  const starValue = node.moduleSlug
    ? ctx.getModuleStarValue(node)
    : ctx.getCategoryStarValue(node);
  const isFavorite = module ? ctx.isFavorite(module.slug) : false;

  return {
    key: node.key,
    'aria-label': accessibleLabel,
    title: (
      <div>
        <Flex dir="row" align="center" gap={10}>
          <div
            aria-hidden="true"
            style={{
              color: '#FFF',
              backgroundColor: $Color.toHexString($Color.from(node.color)),
              borderRadius: '2em',
              padding: '.25em',
            }}
          >
            {getIcon(node)}
          </div>
          <span>{labelText}</span>
          {node.info?.summary && (
            <span
              style={{
                color: '#999',
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
              }}
            >
              {node.info.summary}
            </span>
          )}
          <ProgressStar star={starValue} size={14} />
          {node.moduleSlug && module && (
            <FavoriteButton
              active={isFavorite}
              aria-label={`${isFavorite ? 'Remove' : 'Add'} ${nodeAccessibleLabel} ${isFavorite ? 'from' : 'to'} favorites`}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const slug = module.slug;
                const action = ctx.isFavorite(slug)
                  ? FavoriteGamesAction.Remove
                  : FavoriteGamesAction.Add;
                void ctx.toggleFavorite(slug, action);
              }}
            />
          )}
        </Flex>
      </div>
    ),
    children: [
      ...(node.moduleSlug || node.href
        ? [
            {
              key: `${node.key}#info`,
              'aria-label': `${accessibleLabel}, details`,
              title: (
                <div className="module-info">
                  {!isPlayerLoginType(ctx.loginType) && (
                    <>
                      {displayTags.length > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          {displayTags.map((tag) => (
                            <Tag key={tag}>{tag}</Tag>
                          ))}
                        </div>
                      )}
                      <p>{module?.ariaDescription}</p>
                    </>
                  )}

                  {underConstruction ? (
                    <ComingSoon />
                  ) : (
                    <Toolbar
                      left={
                        node.href && (
                          <GameActions
                            href={withFromParam(node.href, parentKey)}
                            label={nodeAccessibleLabel}
                          />
                        )
                      }
                      right={
                        <Flex align="center" gap={4}>
                          {module?.directionsUrl && (
                            <DirectionsButton
                              url={module.directionsUrl}
                              label={nodeAccessibleLabel}
                            />
                          )}
                          <HelpText
                            module={module}
                            label={nodeAccessibleLabel}
                          />
                        </Flex>
                      }
                    />
                  )}
                </div>
              ),
            },
          ]
        : []),
      ...getEffectiveChildren(node, ctx.moduleTagging)
        .map((n) =>
          toTreeDataNode(
            n,
            node.key,
            ctx,
            node.key === '' ? parentAccessibleLabel : accessibleLabel,
          ),
        )
        .filter((n) => n !== null),
    ],
  };
}

function getIcon(node: StrictNodeModel) {
  const dimensions = { width: '2em', height: '2em' };
  if (node.icon.image) {
    return <img alt="" src={icons[node.icon.image]} style={dimensions} />;
  } else if (node.icon.text) {
    const style = {
      ...dimensions,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1em',
      fontWeight: 'bold',
    };
    if (node.icon.text.length === 1) {
      return <div style={style}>{node.icon.text}</div>;
    } else {
      return <div style={style}>•</div>;
    }
  }
}
